import { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { useNavigate, useLocation } from 'react-router-dom';
import { Navigation } from '../components/Navigation';
import { Breadcrumb } from '../components/Breadcrumb';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { isAdmin, getRoleLabel, UserRole, STAFF_ROLES } from '../utils/permissions';

const GET_CURRENT_USER = gql`
  query GetCurrentUser {
    getCurrentUser {
      id
      email
      firstName
      lastName
      role
    }
  }
`;

const CREATE_USER = gql`
  mutation CreateUser($input: CreateUserInput!) {
    createUser(input: $input) {
      user {
        id
        email
        firstName
        lastName
        phoneNumber
        role
      }
      temporaryPassword
      message
    }
  }
`;

// Type definitions for Apollo queries/mutations
interface CurrentUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
}

interface GetCurrentUserData {
  getCurrentUser: CurrentUser;
}

interface CreateUserData {
  createUser: {
    user: {
      id: string;
      email: string;
      firstName: string;
      lastName: string;
      phoneNumber?: string;
      role: string;
    };
    temporaryPassword: string;
    message?: string;
  };
}

export function UserCreate() {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: userData } = useQuery<GetCurrentUserData>(GET_CURRENT_USER);
  const [createUser, { loading }] = useMutation<CreateUserData>(CREATE_USER);

  // Determine if we're creating from clients page or admin page
  const isFromClientsPage = location.pathname.includes('/clients/new');
  const isFromAdminPage = location.pathname.includes('/admin/users/new');

  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    role: isFromClientsPage ? 'CLIENT' : 'ATTORNEY', // Default role based on context
    street1: '',
    street2: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'US',
    sendWelcomeEmail: true,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [temporaryPassword, setTemporaryPassword] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const user = userData?.getCurrentUser;
  const userIsAdmin = user && isAdmin(user.role);

  // Determine available roles based on user permissions
  const availableRoles = userIsAdmin
    ? [
        { value: UserRole.ADMIN, label: getRoleLabel(UserRole.ADMIN) },
        { value: UserRole.ATTORNEY, label: getRoleLabel(UserRole.ATTORNEY) },
        { value: UserRole.CASE_MANAGER, label: getRoleLabel(UserRole.CASE_MANAGER) },
        { value: UserRole.OFFICE_MANAGER, label: getRoleLabel(UserRole.OFFICE_MANAGER) },
        { value: UserRole.INQUIRY_MANAGER, label: getRoleLabel(UserRole.INQUIRY_MANAGER) },
        { value: UserRole.LEAD_CASE_MANAGER, label: getRoleLabel(UserRole.LEAD_CASE_MANAGER) },
        { value: UserRole.MEDICAL_RECORDS_COORDINATOR, label: getRoleLabel(UserRole.MEDICAL_RECORDS_COORDINATOR) },
        { value: UserRole.PARALEGAL, label: getRoleLabel(UserRole.PARALEGAL) },
        { value: UserRole.CLIENT, label: getRoleLabel(UserRole.CLIENT) },
        { value: UserRole.PROSPECT, label: getRoleLabel(UserRole.PROSPECT) },
      ]
    : [{ value: UserRole.CLIENT, label: getRoleLabel(UserRole.CLIENT) }]; // Staff can only create clients

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.email) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';

    if (!formData.firstName) newErrors.firstName = 'First name is required';
    if (!formData.lastName) newErrors.lastName = 'Last name is required';
    if (!formData.role) newErrors.role = 'Role is required';

    // Address validation (optional but if provided, must be complete)
    if (formData.street1 || formData.city || formData.state || formData.zipCode) {
      if (!formData.street1) newErrors.street1 = 'Street address is required';
      if (!formData.city) newErrors.city = 'City is required';
      if (!formData.state) newErrors.state = 'State is required';
      if (!formData.zipCode) newErrors.zipCode = 'ZIP code is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      const input: any = {
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        role: formData.role,
        sendWelcomeEmail: formData.sendWelcomeEmail,
      };

      if (formData.phoneNumber) {
        input.phoneNumber = formData.phoneNumber;
      }

      // Only include address if at least street1 is provided
      if (formData.street1) {
        input.address = {
          street1: formData.street1,
          street2: formData.street2 || null,
          city: formData.city,
          state: formData.state,
          zipCode: formData.zipCode,
          country: formData.country,
        };
      }

      const result = await createUser({ variables: { input } });

      if (result.data?.createUser) {
        setTemporaryPassword(result.data.createUser.temporaryPassword);
        setShowPassword(true);
      }
    } catch (error: any) {
      console.error('Error creating user:', error);
      alert(`Error creating user: ${error.message}`);
    }
  };

  const handleCopyPassword = () => {
    if (temporaryPassword) {
      navigator.clipboard.writeText(temporaryPassword);
      alert('Temporary password copied to clipboard!');
    }
  };

  const handleDone = () => {
    // Navigate back to appropriate page based on where we came from
    if (isFromClientsPage) {
      navigate('/clients');
    } else if (isFromAdminPage) {
      navigate('/admin/users');
    } else {
      navigate('/dashboard');
    }
  };

  const getPageTitle = () => {
    if (isFromClientsPage) return 'Create New Client';
    if (formData.role === 'CLIENT') return 'Create New Client';
    return 'Create New User';
  };

  const getUserTypeLabel = () => {
    return getRoleLabel(formData.role).toLowerCase();
  };

  if (showPassword) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation user={user} />
        
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Card>
            <CardHeader>
              <CardTitle>User Created Successfully!</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-green-800">
                    ✓ {formData.firstName} {formData.lastName} has been created as a {getUserTypeLabel()}.
                  </p>
                  {formData.sendWelcomeEmail && (
                    <p className="text-green-700 text-sm mt-2">
                      A welcome email with login instructions has been sent to {formData.email}
                    </p>
                  )}
                </div>

                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-2">Temporary Password</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 bg-white px-4 py-2 rounded border border-gray-300 font-mono text-lg">
                      {temporaryPassword}
                    </code>
                    <Button onClick={handleCopyPassword} variant="outline">
                      Copy
                    </Button>
                  </div>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-yellow-800 text-sm">
                    <strong>Important:</strong> The user will be required to change this password upon their first login.
                    {!formData.sendWelcomeEmail && ' Make sure to share this password with the user securely.'}
                  </p>
                </div>

                <div className="flex gap-2">
                  <Button onClick={handleDone} className="flex-1">
                    Done
                  </Button>
                  <Button onClick={() => window.location.reload()} variant="outline" className="flex-1">
                    Create Another User
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation user={user} />
      
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumb />

        <Card>
          <CardHeader>
            <CardTitle>{getPageTitle()}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Role Selection (only show if admin or from admin page) */}
              {(userIsAdmin || isFromAdminPage) && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">User Role</h3>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Role <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="role"
                      value={formData.role}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-aalaw-red ${
                        errors.role ? 'border-red-500' : 'border-gray-300'
                      }`}
                      disabled={!userIsAdmin}
                    >
                      {availableRoles.map((role) => (
                        <option key={role.value} value={role.value}>
                          {role.label}
                        </option>
                      ))}
                    </select>
                    {errors.role && (
                      <p className="text-red-500 text-xs mt-1">{errors.role}</p>
                    )}
                    {!userIsAdmin && (
                      <p className="text-xs text-gray-500 mt-1">
                        You can only create client accounts. Contact an admin to create staff accounts.
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Basic Information */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      First Name <span className="text-red-500">*</span>
                    </label>
                    <Input
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      className={errors.firstName ? 'border-red-500' : ''}
                    />
                    {errors.firstName && (
                      <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Last Name <span className="text-red-500">*</span>
                    </label>
                    <Input
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      className={errors.lastName ? 'border-red-500' : ''}
                    />
                    {errors.lastName && (
                      <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={errors.email ? 'border-red-500' : ''}
                    />
                    {errors.email && (
                      <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone Number
                    </label>
                    <Input
                      type="tel"
                      name="phoneNumber"
                      value={formData.phoneNumber}
                      onChange={handleChange}
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </div>
              </div>

              {/* Address (Optional) */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Address (Optional)</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Street Address
                    </label>
                    <Input
                      name="street1"
                      value={formData.street1}
                      onChange={handleChange}
                      placeholder="123 Main St"
                      className={errors.street1 ? 'border-red-500' : ''}
                    />
                    {errors.street1 && (
                      <p className="text-red-500 text-xs mt-1">{errors.street1}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Street Address Line 2
                    </label>
                    <Input
                      name="street2"
                      value={formData.street2}
                      onChange={handleChange}
                      placeholder="Apt, Suite, Unit, etc."
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        City
                      </label>
                      <Input
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        className={errors.city ? 'border-red-500' : ''}
                      />
                      {errors.city && (
                        <p className="text-red-500 text-xs mt-1">{errors.city}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        State
                      </label>
                      <Input
                        name="state"
                        value={formData.state}
                        onChange={handleChange}
                        placeholder="CA"
                        maxLength={2}
                        className={errors.state ? 'border-red-500' : ''}
                      />
                      {errors.state && (
                        <p className="text-red-500 text-xs mt-1">{errors.state}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        ZIP Code
                      </label>
                      <Input
                        name="zipCode"
                        value={formData.zipCode}
                        onChange={handleChange}
                        placeholder="12345"
                        className={errors.zipCode ? 'border-red-500' : ''}
                      />
                      {errors.zipCode && (
                        <p className="text-red-500 text-xs mt-1">{errors.zipCode}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Country
                      </label>
                      <Input
                        name="country"
                        value={formData.country}
                        onChange={handleChange}
                        placeholder="US"
                        maxLength={2}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Email Options */}
              <div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    name="sendWelcomeEmail"
                    checked={formData.sendWelcomeEmail}
                    onChange={handleChange}
                    className="w-4 h-4 text-aalaw-red border-gray-300 rounded focus:ring-aalaw-red"
                  />
                  <span className="text-sm text-gray-700">
                    Send welcome email with login instructions
                  </span>
                </label>
              </div>

              {/* Actions */}
              <div className="flex gap-4 pt-4 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    if (isFromClientsPage) {
                      navigate('/clients');
                    } else if (isFromAdminPage) {
                      navigate('/admin/users');
                    } else {
                      navigate('/dashboard');
                    }
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="flex-1"
                >
                  {loading ? 'Creating...' : `Create ${getUserTypeLabel()}`}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
